import { rollbar, toReportableError } from '@/dependencies/rollbar';

export type ReportContext = Record<string, unknown>;

/**
 * Reports a thrown value to Rollbar with a real title.
 *
 * Only failures that reach Vue's `app.config.errorHandler` are titled by the rollbar
 * plugin. Anything that is caught before that — a `computedAsync` rejection, a `.catch()`
 * in a store — has to come through here, otherwise Rollbar records it with no class and
 * no message and every unrelated failure collapses into one undiagnosable item.
 *
 * The original payload is attached untouched; the title deliberately leaves the API's
 * translated `message` out so one bug does not group once per language.
 */
export function reportError(thrown: unknown, context: ReportContext = {}): Error {
    const reportable = toReportableError(thrown);

    rollbar.error(reportable, { ...context, original: thrown });

    // Rollbar is disabled without an access token (local, e2e), so without this the failure
    // would be silent while developing. Rollbar treats console output as telemetry, not as a
    // second item, so this does not re-open the duplicate-reporting problem.
    console.error(reportable);

    return reportable;
}

/**
 * `onError` handler for `computedAsync`.
 *
 * @vueuse/core defaults `onError` to `globalThis.reportError`, which re-throws the value as
 * an uncaught error — bypassing Vue's `errorHandler` entirely. When the value is a rejected
 * API payload rather than an `Error`, Rollbar files it as `(unknown): [object Object]`.
 * Pass this instead so the failure arrives titled:
 *
 * ```ts
 * computedAsync(async () => { ... }, null, { onError: reportAsyncError('offers.hasJobs') });
 * ```
 *
 * @param source Names the call site, so two stores failing on the same endpoint stay apart.
 */
export function reportAsyncError(source: string, context: ReportContext = {}): (thrown: unknown) => void {
    return (thrown: unknown) => {
        reportError(thrown, { ...context, source });
    };
}
